import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  DollarSign,
  Plus,
  Trash2,
  Pencil,
  Loader2,
  Receipt,
  Camera,
  TrendingUp,
  TrendingDown,
  X,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { expenseApi, estimateApi, changeOrderApi, subcontractorApi } from '../lib/api';

const CATEGORIES = [
  { value: 'materials', label: 'Materials' },
  { value: 'labor', label: 'Labor' },
  { value: 'subcontractor', label: 'Subcontractor' },
  { value: 'equipment_rental', label: 'Equipment Rental' },
  { value: 'permits', label: 'Permits' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'tools', label: 'Tools' },
  { value: 'disposal', label: 'Disposal' },
  { value: 'other', label: 'Other' },
];

const CATEGORY_COLORS = {
  materials: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  labor: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  subcontractor: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  equipment_rental: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  permits: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  delivery: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
  tools: 'bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-200',
  disposal: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  other: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
};

function formatCurrency(val) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

export default function ProjectCosts({ projectId }) {
  const [expenses, setExpenses] = useState([]);
  const [budget, setBudget] = useState(null);
  const [scopeOptions, setScopeOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterCat, setFilterCat] = useState('all');

  useEffect(() => {
    loadData();
  }, [projectId]);

  const loadData = async () => {
    try {
      const [expRes, budRes] = await Promise.all([
        expenseApi.getForProject(projectId),
        expenseApi.getBudget(projectId),
      ]);
      setExpenses(expRes.data);
      setBudget(budRes.data);
      // Scope options for assigning expenses (job costing): estimates + change orders
      try {
        const [estRes, coRes] = await Promise.all([
          estimateApi.getForProject(projectId),
          changeOrderApi.getForProject(projectId),
        ]);
        const ests = (estRes.data || []).map((e) => ({
          type: 'estimate', id: e.id,
          label: `Estimate: ${e.name || e.estimate_number || e.id.slice(0, 6)}`,
        }));
        const cos = (coRes.data || []).map((c) => ({
          type: 'change_order', id: c.id,
          label: `${c.type === 'time_materials' ? 'T&M' : 'CO'}: ${c.co_number} — ${c.title}`,
        }));
        setScopeOptions([...ests, ...cos]);
      } catch (e) {
        setScopeOptions([]);
      }
    } catch (err) {
      console.error('Failed to load costs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await expenseApi.delete(expenseId);
      toast.success('Expense deleted');
      loadData();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const filtered = filterCat === 'all' ? expenses : expenses.filter(e => e.category === filterCat);

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="project-costs">
      {/* Budget Summary */}
      {budget && <BudgetSummaryCard budget={budget} />}

      {/* Expenses Section */}
      <Card className="rounded-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Receipt className="h-4 w-4" />
              Job Expenses
              {expenses.length > 0 && <Badge variant="secondary">{expenses.length}</Badge>}
            </span>
            <div className="flex items-center gap-2">
              <Select value={filterCat} onValueChange={setFilterCat}>
                <SelectTrigger className="w-36 h-8 text-xs rounded-sm" data-testid="expense-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                onClick={() => { setEditing(null); setShowForm(!showForm); }}
                className="rounded-sm bg-orange-500 hover:bg-orange-600"
                data-testid="add-expense-btn"
              >
                <Plus className="h-4 w-4 mr-1" /> Add Expense
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Add/Edit Expense Form */}
          {showForm && (
            <ExpenseForm
              projectId={projectId}
              scopeOptions={scopeOptions}
              editing={editing}
              onSaved={() => { setShowForm(false); setEditing(null); loadData(); }}
              onCancel={() => { setShowForm(false); setEditing(null); }}
            />
          )}

          {/* Expense List */}
          {filtered.length === 0 ? (
            <p className="text-center text-muted-foreground py-6 text-sm">
              {expenses.length === 0 ? 'No expenses tracked yet. Add your first job cost.' : 'No expenses in this category.'}
            </p>
          ) : (
            <div className="divide-y">
              {filtered.map(expense => (
                <div key={expense.id} className="flex items-center justify-between py-3 group" data-testid={`expense-${expense.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    {expense.has_receipt && expense.receipt_url ? (
                      <button onClick={() => window.open(expense.receipt_url, '_blank')}
                        className="h-10 w-10 rounded-sm bg-muted flex items-center justify-center shrink-0 hover:bg-muted/80">
                        <Camera className="h-4 w-4 text-muted-foreground" />
                      </button>
                    ) : (
                      <div className="h-10 w-10 rounded-sm bg-muted/50 flex items-center justify-center shrink-0">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{expense.description}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge className={`text-[10px] ${CATEGORY_COLORS[expense.category]}`}>
                          {CATEGORIES.find(c => c.value === expense.category)?.label || expense.category}
                        </Badge>
                        {expense.vendor && <span className="text-xs text-muted-foreground">{expense.vendor}</span>}
                        <span className="text-xs text-muted-foreground">{expense.date}</span>
                        {expense.scope_label && (
                          <Badge variant="outline" className="text-[10px] max-w-[200px] truncate" title={expense.scope_label} data-testid={`expense-scope-${expense.id}`}>
                            {expense.scope_label}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-sm font-semibold tabular-nums">{formatCurrency(expense.amount)}</span>
                    <Button size="sm" variant="ghost"
                      className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground"
                      onClick={() => { setEditing(expense); setShowForm(true); }}
                      data-testid={`edit-expense-${expense.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost"
                      className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive"
                      onClick={() => handleDelete(expense.id)}
                      data-testid={`delete-expense-${expense.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function BudgetSummaryCard({ budget }) {
  const profitPositive = budget.profit_estimate >= 0;
  const hasContract = budget.contract_value > 0;
  const hasEstimate = budget.estimate_total > 0;

  return (
    <Card className="rounded-sm" data-testid="budget-summary">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <DollarSign className="h-4 w-4" />
          Budget vs Actual
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {hasContract && (
            <div>
              <p className="text-xs text-muted-foreground">Contract Value</p>
              <p className="text-lg font-bold tabular-nums">{formatCurrency(budget.contract_value)}</p>
            </div>
          )}
          {budget.approved_change_orders > 0 && (
            <div data-testid="budget-revised-total">
              <p className="text-xs text-muted-foreground">Revised Total <span className="text-[10px]">(+CO {formatCurrency(budget.approved_change_orders)})</span></p>
              <p className="text-lg font-bold tabular-nums text-blue-600">{formatCurrency(budget.revised_contract_value)}</p>
            </div>
          )}
          {hasEstimate && (
            <div>
              <p className="text-xs text-muted-foreground">Estimate Total</p>
              <p className="text-lg font-bold tabular-nums text-orange-600">{formatCurrency(budget.estimate_total)}</p>
            </div>
          )}
          <div>
            <p className="text-xs text-muted-foreground">Total Expenses</p>
            <p className="text-lg font-bold tabular-nums text-red-600">{formatCurrency(budget.total_expenses)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Invoiced</p>
            <p className="text-lg font-bold tabular-nums">{formatCurrency(budget.total_invoiced)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Collected</p>
            <p className="text-lg font-bold tabular-nums text-green-600">{formatCurrency(budget.total_paid)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Est. Profit</p>
            <p className={`text-lg font-bold tabular-nums flex items-center gap-1 ${profitPositive ? 'text-green-600' : 'text-red-600'}`}>
              {profitPositive ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
              {formatCurrency(budget.profit_estimate)}
            </p>
          </div>
        </div>

        {/* Category breakdown bar */}
        {Object.keys(budget.expenses_by_category).length > 0 && (
          <div className="mt-4">
            <p className="text-xs text-muted-foreground mb-2">Expenses by Category</p>
            <div className="space-y-1.5">
              {Object.entries(budget.expenses_by_category)
                .sort((a, b) => b[1] - a[1])
                .map(([cat, amount]) => {
                  const pct = budget.total_expenses > 0 ? (amount / budget.total_expenses * 100) : 0;
                  const catLabel = CATEGORIES.find(c => c.value === cat)?.label || cat;
                  return (
                    <div key={cat} className="flex items-center gap-2 text-xs">
                      <span className="w-24 truncate text-muted-foreground">{catLabel}</span>
                      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-orange-500 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-20 text-right tabular-nums font-medium">{formatCurrency(amount)}</span>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ExpenseForm({ projectId, scopeOptions = [], editing = null, onSaved, onCancel }) {
  const [form, setForm] = useState({
    description: editing?.description || '',
    amount: editing?.amount != null ? String(editing.amount) : '',
    category: editing?.category || 'materials',
    vendor: editing?.vendor || '',
    date: editing?.date || new Date().toISOString().split('T')[0],
    notes: editing?.notes || '',
    scope: editing?.scope_type && editing?.scope_id ? `${editing.scope_type}:${editing.scope_id}` : 'general',
    subcontractor_id: editing?.subcontractor_id || '',
  });
  const [receipt, setReceipt] = useState(null);
  const [removeReceipt, setRemoveReceipt] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [subs, setSubs] = useState([]);
  const receiptRef = useRef(null);
  const scanRef = useRef(null);
  const isEdit = !!editing;

  const handleScan = async (file) => {
    if (!file) return;
    setScanning(true);
    setReceipt(file);
    setRemoveReceipt(false);
    try {
      const res = await expenseApi.scanReceipt(file);
      const d = res.data || {};
      setForm((f) => ({
        ...f,
        description: d.description || d.vendor || f.description,
        amount: d.total ? String(d.total) : f.amount,
        vendor: d.vendor || f.vendor,
        date: d.date || f.date,
        category: d.category || f.category,
      }));
      toast.success('Receipt scanned — review the details and save');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not read the receipt. Enter details manually.');
    } finally {
      setScanning(false);
    }
  };

  useEffect(() => {
    if (form.category === 'subcontractor' && subs.length === 0) {
      subcontractorApi.getAll().then((res) => setSubs(res.data || [])).catch(() => {});
    }
  }, [form.category]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description.trim() || !form.amount) {
      toast.error('Description and amount are required');
      return;
    }
    setSaving(true);
    try {
      const chosen = scopeOptions.find((s) => `${s.type}:${s.id}` === form.scope);
      const payload = {
        ...form,
        amount: parseFloat(form.amount),
        scope_type: chosen ? chosen.type : '',
        scope_id: chosen ? chosen.id : '',
        scope_label: chosen ? chosen.label : '',
        subcontractor_id: form.category === 'subcontractor' ? form.subcontractor_id : '',
        receipt: receipt,
      };
      if (isEdit) {
        await expenseApi.update(editing.id, { ...payload, remove_receipt: removeReceipt });
        toast.success('Expense updated');
      } else {
        await expenseApi.create(projectId, payload);
        toast.success('Expense added');
      }
      onSaved();
    } catch (err) {
      toast.error(`Failed: ${err.response?.data?.detail || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="border rounded-sm p-4 bg-muted/30 space-y-3" data-testid="expense-form">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">{isEdit ? 'Edit Expense' : 'New Expense'}</p>
        <div className="flex items-center gap-2">
          <input ref={scanRef} type="file" accept="image/*,application/pdf,.pdf,.heic,.heif" className="hidden"
            onChange={e => { handleScan(e.target.files?.[0] || null); e.target.value = ''; }} />
          <Button type="button" size="sm" variant="outline" className="h-7 text-xs rounded-sm"
            onClick={() => scanRef.current?.click()} disabled={scanning} data-testid="scan-receipt-btn">
            {scanning ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
            {scanning ? 'Reading…' : 'Scan Receipt'}
          </Button>
          <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Description *</Label>
          <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})}
            placeholder="e.g. Lumber from Home Depot" className="h-8 rounded-sm text-sm mt-1" data-testid="expense-description" />
        </div>
        <div>
          <Label className="text-xs">Amount *</Label>
          <Input type="number" step="0.01" min="0" value={form.amount}
            onChange={e => setForm({...form, amount: e.target.value})}
            placeholder="0.00" className="h-8 rounded-sm text-sm mt-1" data-testid="expense-amount" />
        </div>
        <div>
          <Label className="text-xs">Category</Label>
          <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
            <SelectTrigger className="h-8 rounded-sm text-sm mt-1" data-testid="expense-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {form.category === 'subcontractor' && (
          <div>
            <Label className="text-xs">Paid to Subcontractor <span className="text-muted-foreground">(for 1099)</span></Label>
            <Select value={form.subcontractor_id || 'none'} onValueChange={v => setForm({...form, subcontractor_id: v === 'none' ? '' : v})}>
              <SelectTrigger className="h-8 rounded-sm text-sm mt-1" data-testid="expense-subcontractor">
                <SelectValue placeholder="Select subcontractor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Not linked —</SelectItem>
                {subs.map(s => <SelectItem key={s.id} value={s.id}>{s.company_name || s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label className="text-xs">Vendor</Label>
          <Input value={form.vendor} onChange={e => setForm({...form, vendor: e.target.value})}
            placeholder="e.g. Home Depot" className="h-8 rounded-sm text-sm mt-1" data-testid="expense-vendor" />
        </div>
        <div>
          <Label className="text-xs">Date</Label>
          <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})}
            className="h-8 rounded-sm text-sm mt-1" data-testid="expense-date" />
        </div>
        <div>
          <Label className="text-xs">Receipt Photo</Label>
          <div className="flex items-center gap-2 mt-1">
            <input ref={receiptRef} type="file" accept="image/*,.pdf" className="hidden"
              onChange={e => { setReceipt(e.target.files?.[0] || null); setRemoveReceipt(false); }} />
            <Button type="button" size="sm" variant="outline" className="h-8 text-xs rounded-sm"
              onClick={() => receiptRef.current?.click()} data-testid="expense-receipt-btn">
              <Camera className="h-3.5 w-3.5 mr-1" />
              {receipt ? receipt.name.slice(0, 20)
                : (isEdit && editing.has_receipt && !removeReceipt) ? 'Replace Receipt' : 'Attach Receipt'}
            </Button>
            {receipt && (
              <Button type="button" size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setReceipt(null)}>
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
            {isEdit && editing.has_receipt && !receipt && !removeReceipt && (
              <Button type="button" size="sm" variant="ghost" className="h-8 text-xs text-destructive"
                onClick={() => setRemoveReceipt(true)} data-testid="expense-remove-receipt-btn">
                Remove
              </Button>
            )}
            {removeReceipt && <span className="text-xs text-muted-foreground">Will be removed</span>}
          </div>
        </div>
      </div>
      <div>
        <Label className="text-xs">Notes</Label>
        <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
          placeholder="Optional notes" className="h-8 rounded-sm text-sm mt-1" />
      </div>
      <div>
        <Label className="text-xs">Assign to (for job costing)</Label>
        <Select value={form.scope} onValueChange={v => setForm({...form, scope: v})}>
          <SelectTrigger className="h-8 rounded-sm text-sm mt-1" data-testid="expense-scope">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="general">General / Project-wide</SelectItem>
            {scopeOptions.map((s) => (
              <SelectItem key={`${s.type}:${s.id}`} value={`${s.type}:${s.id}`}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex justify-end">
        <Button type="submit" size="sm" disabled={saving} className="rounded-sm bg-orange-500 hover:bg-orange-600" data-testid="save-expense-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
          {saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Add Expense')}
        </Button>
      </div>
    </form>
  );
}
